import type {
  FetchBookingEventTemplateByIdArgs,
  AssignBookingEventTemplateArgs,
  CreateBookingEventTemplateArgs,
  DeleteBookingEventTemplateArgs,
  FetchBookingEventTemplatesArgs,
  UpdateBookingEventTemplateArgs,
  ApiBookingEventTemplateDetail,
  ApiBookingEventTemplate,
} from './types/bookingEventTemplates.types';
import type {
  ApiBookingEventWarning,
  ApiBookingEvent,
} from './types/bookingEvents.types';
import type {
  FetchSignedUploadUrlArgs,
  UiSignedUploadUrl,
} from './types/files.types';
import type { BaseApiResponse } from './types/api.types';

import { destroy, patch, post, get } from './axios';

const templatesUrl = (organizationId: string) =>
  `/v2/organizations/${organizationId}/booking-event-templates`;

export const fetchBookingEventTemplates = async ({
  organizationId,
  search,
}: FetchBookingEventTemplatesArgs) => {
  const response = await get<BaseApiResponse<ApiBookingEventTemplate[]>>(
    templatesUrl(organizationId),
    { query: search ? { search } : undefined },
  );
  return response?.data ?? [];
};

export const fetchBookingEventTemplateById = async ({
  bookingEventTemplateId,
  organizationId,
}: FetchBookingEventTemplateByIdArgs) => {
  const response = await get<BaseApiResponse<ApiBookingEventTemplateDetail>>(
    `${templatesUrl(organizationId)}/${bookingEventTemplateId}`,
  );
  return response?.data ?? null;
};

export const createBookingEventTemplate = async ({
  organizationId,
  ...payload
}: CreateBookingEventTemplateArgs) => {
  const response = await post<
    ApiBookingEventTemplate,
    Omit<CreateBookingEventTemplateArgs, 'organizationId'>
  >(templatesUrl(organizationId), payload);
  return response?.data ?? null;
};

export const updateBookingEventTemplate = async ({
  bookingEventTemplateId,
  organizationId,
  ...payload
}: UpdateBookingEventTemplateArgs) => {
  const response = await patch<
    ApiBookingEventTemplate,
    Omit<
      UpdateBookingEventTemplateArgs,
      'bookingEventTemplateId' | 'organizationId'
    >
  >(`${templatesUrl(organizationId)}/${bookingEventTemplateId}`, payload);
  return response?.data ?? null;
};

export const deleteBookingEventTemplate = async ({
  bookingEventTemplateId,
  organizationId,
}: DeleteBookingEventTemplateArgs) => {
  return destroy(`${templatesUrl(organizationId)}/${bookingEventTemplateId}`);
};

/**
 * "Assign to schedule". `requiresConfirmation`/`data:null` never reach the
 * caller through the typed `data` field — same reason `createBookingEvent`
 * reads `warnings` off the raw response — so both are pulled off the response
 * the same way.
 */
export const assignBookingEventTemplate = async ({
  bookingEventTemplateId,
  organizationId,
  ...payload
}: AssignBookingEventTemplateArgs) => {
  const response = await post<
    ApiBookingEvent[] | null,
    Omit<
      AssignBookingEventTemplateArgs,
      'bookingEventTemplateId' | 'organizationId'
    >
  >(
    `${templatesUrl(organizationId)}/${bookingEventTemplateId}/assign`,
    payload,
  );
  const raw = response as unknown as {
    requiresConfirmation?: boolean;
    warnings?: ApiBookingEventWarning[];
    data?: ApiBookingEvent[] | null;
  };
  return {
    events: raw?.data ?? null,
    requiresConfirmation: raw?.requiresConfirmation ?? false,
    warnings: raw?.warnings ?? [],
  };
};

export const fetchBookingEventTemplateSignedUploadUrl = async ({
  mediaType,
  organizationId,
  target,
}: FetchSignedUploadUrlArgs): Promise<UiSignedUploadUrl> => {
  if (!organizationId) {
    throw new Error('Organization is required for booking event templates');
  }
  const response = await get<{ data: UiSignedUploadUrl }>(
    `${templatesUrl(organizationId)}/signed-upload-url?mediaType=${mediaType}${target ? `&target=${target}` : ''}`,
  );
  return response?.data as UiSignedUploadUrl;
};
